import clsx from "clsx";
import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getFarms } from "regen-api-ts-client";

import { IconButton } from "../../components/buttons";
import { HDiv, VDiv } from "../../components/Div";
import NavSection from "../../components/layout/NavSection";
import {
  adminSettingsItems,
  generateFarmSettingsItems,
  userProfileItems,
} from "../../components/layout/navSectionData";
import NavTabs from "../../components/layout/NavTabs";
import OnboardingNavContent from "../../components/layout/OnboardingNavContent";
import { isOnboarding } from "../../util";
import { useCurrentFarm, useRegenQuery } from "../../util/apiHooks";
import { ONBOARDING_STEPS } from "../../util/constants";
import {
  useClickOutside,
  useHasSomeRoles,
  useIsWidescreen,
} from "../../util/hooks";

interface LeftNavProps {
  showMobileNav: boolean;
  closeMobileNav: () => void;
}

const LeftNav = ({ showMobileNav, closeMobileNav }: LeftNavProps) => {
  const { farmId } = useParams();
  const farm = useCurrentFarm(farmId);
  const { data: farmList } = useRegenQuery(getFarms, {});
  const { data: isAdminOrStaff } = useHasSomeRoles(["admin", "staff"]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isWidescreen = useIsWidescreen();
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, closeMobileNav, showMobileNav);

  const showAdminStyle =
    isAdminOrStaff &&
    (pathname.includes("admin") || pathname.includes("user-profile"));

  const logoPath = showAdminStyle ? "/vayda-logo-admin.svg" : "/vayda-logo.svg";

  let currentOnboardingStep = ONBOARDING_STEPS.findIndex((step) =>
    pathname.includes(step.path),
  );
  if (currentOnboardingStep < 0) {
    currentOnboardingStep = 0;
  }

  const growerFarmId = useMemo(() => {
    if (farmId) {
      return farmId;
    }
    return !isAdminOrStaff && farmList && farmList.length > 0
      ? farmList[0].id
      : undefined;
  }, [farmId, isAdminOrStaff, farmList]);

  useEffect(() => {
    if (showMobileNav && isWidescreen) {
      closeMobileNav();
    }
  }, [isWidescreen, showMobileNav, closeMobileNav]);

  const subheaderText = useMemo(() => {
    if (isOnboarding()) {
      return ONBOARDING_STEPS[currentOnboardingStep].label;
    }
    if (farm?.label) {
      return farm.label;
    } else {
      return isAdminOrStaff ? "Admin" : "";
    }
  }, [isAdminOrStaff, currentOnboardingStep, farm]);

  const showAdminTabs =
    (isAdminOrStaff && !farmId) || pathname.includes("admin");

  const classes = clsx(
    "justify-between gap-8",
    "h-full overflow-y-auto p-4 z-30 font-medium",
    {
      "bg-white border-r border-neutral-300": !showAdminStyle,
      "bg-admin": showAdminStyle,
    },
  );

  const logoComponent = (
    <HDiv className="items-start justify-between gap-4">
      <div
        data-component="LeftNav.logo"
        className="cursor-pointer"
        onClick={() => navigate("/")}
      >
        <img src={logoPath} alt={"Vayda logo"} className="h-10" />
      </div>
      {!isWidescreen && (
        <IconButton
          onClick={closeMobileNav}
          name="x"
          data-testid="MobileCloseLeftNavButton"
          altText="Close Left Nav"
          size="lg"
          className={clsx({
            "text-white": showAdminStyle,
            "text-primary": !showAdminStyle,
          })}
        />
      )}
    </HDiv>
  );

  const subheaderComponent = subheaderText && (
    <div
      data-component="LeftNav.subheader"
      className={`text-sm font-semibold ${
        showAdminStyle ? "text-secondary-medium" : "text-primary"
      }`}
    >
      {subheaderText}
    </div>
  );

  return (
    <div
      className={`flex flex-col ${classes}`}
      data-component="LeftNav"
      ref={ref}
    >
      <VDiv className="gap-8">
        <VDiv className="gap-1">
          {logoComponent}
          {subheaderComponent}
        </VDiv>
        {isOnboarding() ? (
          <OnboardingNavContent currentStepIndex={currentOnboardingStep} />
        ) : (
          <NavTabs
            showAdminTabs={showAdminTabs}
            onNavLinkClick={closeMobileNav}
          />
        )}
      </VDiv>
      {!isOnboarding() && (
        <VDiv className="gap-3">
          {!!growerFarmId && (
            <>
              <NavSection
                title="Farm Settings"
                items={generateFarmSettingsItems(growerFarmId)}
                onNavLinkClick={closeMobileNav}
              />
              <hr className="w-full border-gray-300" />
            </>
          )}
          {isAdminOrStaff && !showAdminTabs && (
            <>
              <NavSection
                title="Admin Settings"
                items={adminSettingsItems}
                onNavLinkClick={closeMobileNav}
              />
              <hr className="w-full border-gray-300" />
            </>
          )}
          <NavSection
            title="Profile"
            items={userProfileItems}
            onNavLinkClick={closeMobileNav}
          />
        </VDiv>
      )}
    </div>
  );
};

export default LeftNav;
